import React, { useState } from "react";
import { useVenue, SKIP_VOTES_NEEDED } from "../context/VenueContext";
import { useAuth } from "../context/AuthContext";
import { submitVibeVote, voteToSkip } from "../services/queueService";
import FallbackPlaylistPanel from "./FallbackPlaylistPanel";

function formatTime(ms) {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export default function NowPlayingScreen({ setScreen, onNotif }) {
  const {
    venueId, nowPlaying, isPlaying, positionMs, durationMs, skipVoters,
    isVenueMode, playerReady, playerError, queue, vibeScore, fallback,
    handlePlayPause, handleStartMusic, handleRestart, handleSkip,
  } = useVenue();
  const { user } = useAuth();
  const [userVibeVote, setUserVibeVote] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);

  const progress = durationMs ? Math.min(100, (positionMs / durationMs) * 100) : 0;
  const userVotedSkip = !!user && skipVoters.includes(user.uid);
  const skipVotes = skipVoters.length;
  const upNext = queue[0];

  const handleVibe = async (type) => {
    if (userVibeVote === type) { onNotif("Already voted", "error"); return; }
    await submitVibeVote(venueId, type, userVibeVote);
    setUserVibeVote(type);
    onNotif(type === "good" ? "Good vibe! 🔥" : "Vibe noted 💀");
  };

  const handleSkipVote = async () => {
    if (!nowPlaying) return;
    if (!user) { onNotif("Join the venue to vote", "error"); return; }
    if (userVotedSkip) return;
    await voteToSkip(venueId, user.uid);
    if (skipVotes + 1 >= SKIP_VOTES_NEEDED) onNotif("Vote passed — skipping song");
    else onNotif(`Skip vote cast (${skipVotes + 1}/${SKIP_VOTES_NEEDED} needed)`);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0A0A0A", fontFamily: "'DM Sans', system-ui, sans-serif", paddingBottom: 60 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        .np-back { background: none; border: none; color: rgba(255,255,255,0.5); cursor: pointer; font-size: 20px; padding: 4px; font-family: 'DM Sans', sans-serif; transition: color 0.15s; }
        .np-back:hover { color: rgba(255,255,255,0.8); }
        .np-cover-wrap { position: relative; padding: 0 40px; margin-top: 8px; max-width: 600px; margin-left: auto; margin-right: auto; }
        .np-cover-glow { position: absolute; inset: -20px 0; background: radial-gradient(ellipse at 50% 60%, rgba(29,185,84,0.12) 0%, transparent 70%); pointer-events: none; z-index: 0; }
        .np-cover { width: 100%; aspect-ratio: 1; border-radius: 20px; object-fit: cover; box-shadow: 0 24px 60px rgba(0,0,0,0.5); position: relative; z-index: 1; }
        .np-cover-placeholder { width: 100%; aspect-ratio: 1; border-radius: 20px; background: #1a1a1a; display: flex; align-items: center; justify-content: center; font-size: 72px; box-shadow: 0 24px 60px rgba(0,0,0,0.5); }
        .np-action-btn { flex: 1; padding: 12px 8px; border-radius: 20px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.7); font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 4px; transition: all 0.15s; }
        .np-action-btn:active { transform: scale(0.97); }
        .np-action-btn.voted { border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.08); color: #fff; }
        .skip-bar { height: 2px; background: rgba(255,255,255,0.1); border-radius: 1px; margin-top: 4px; overflow: hidden; width: 100%; }
        .skip-fill { height: 100%; background: rgba(255,255,255,0.5); border-radius: 1px; transition: width 0.3s; }
        .vibe-btn { flex: 1; padding: 14px 8px; border-radius: 20px; font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 4px; transition: all 0.15s; }
        .vibe-btn:active { transform: scale(0.97); }
        .np-play-btn { width: 60px; height: 60px; border-radius: 50%; background: #fff; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 22px; transition: transform 0.15s; color: #0A0A0A; }
        .np-play-btn:active { transform: scale(0.93); }
        .np-play-btn.disabled { background: rgba(255,255,255,0.2); cursor: not-allowed; color: rgba(255,255,255,0.4); }
        .np-ctrl-btn { background: none; border: none; color: rgba(255,255,255,0.4); cursor: pointer; font-size: 22px; padding: 8px; transition: color 0.15s; }
        .np-ctrl-btn:active { transform: scale(0.93); }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .modal-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: flex-end; z-index: 100; backdrop-filter: blur(4px); animation: fadeIn 0.2s ease; }
        .modal-sheet { background: #141414; border-top: 1px solid rgba(255,255,255,0.08); width: 100%; border-radius: 24px 24px 0 0; padding: 24px 24px 40px; animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1); }
      `}</style>

      <div style={{ padding: "32px 40px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", maxWidth: 600, margin: "0 auto", width: "100%" }}>
        <button className="np-back" onClick={() => setScreen("home")}>‹</button>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.3)" }}>Now Playing</div>
        <div style={{ width: 28 }} />
      </div>

      <div className="np-cover-wrap">
        <div className="np-cover-glow" />
        {nowPlaying?.cover
          ? <img className="np-cover" src={nowPlaying.cover} alt="" />
          : <div className="np-cover-placeholder" style={{ position: "relative", zIndex: 1 }}>🎵</div>
        }
      </div>

      <div style={{ padding: "24px 40px 0", maxWidth: 600, margin: "0 auto", width: "100%" }}>
        {nowPlaying && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: isPlaying ? "#1DB954" : "rgba(255,255,255,0.4)" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: isPlaying ? "#1DB954" : "rgba(255,255,255,0.3)" }} />
            {isPlaying ? "Live" : "Paused"}
          </div>
        )}
        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(28px, 4vw, 42px)", color: "#fff", letterSpacing: -1.5, lineHeight: 1.1 }}>
          {nowPlaying?.title || "Nothing playing"}
        </div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.5)", marginTop: 6, lineHeight: 1.5 }}>
          {nowPlaying?.artist || "Request a song to get the music going"}
        </div>
        {nowPlaying?.source === "playlist" && (
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 6 }}>From the venue's playlist</div>
        )}
      </div>

      <div style={{ padding: "20px 40px 0", maxWidth: 600, margin: "0 auto", width: "100%" }}>
        <div style={{ height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "#1DB954", borderRadius: 2, transition: "width 0.1s linear" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
          <span>{formatTime(positionMs)}</span>
          <span>{nowPlaying ? formatTime(durationMs) : "0:00"}</span>
        </div>
      </div>

      <div style={{ padding: "16px 40px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 32, maxWidth: 600, margin: "0 auto", width: "100%" }}>
        <button className="np-ctrl-btn" style={{ opacity: isVenueMode && nowPlaying ? 1 : 0.3 }}
          onClick={() => { if (isVenueMode && nowPlaying) handleRestart(); }}>⏮</button>
        <button className={`np-play-btn ${!isVenueMode || !nowPlaying ? "disabled" : ""}`}
          onClick={() => { if (isVenueMode && nowPlaying) handlePlayPause(); }}>
          {isPlaying ? "⏸" : "▶"}
        </button>
        <button className="np-ctrl-btn" style={{ opacity: isVenueMode && nowPlaying ? 1 : 0.3 }}
          onClick={() => { if (isVenueMode && nowPlaying) handleSkip(); }}>⏭</button>
      </div>

      {isVenueMode ? (
        <div style={{ margin: "16px auto 0", maxWidth: 520, padding: "0 40px", textAlign: "center" }}>
          {playerError && (
            <div style={{ marginBottom: 10, padding: "10px 14px", background: "rgba(255,107,107,0.1)", border: "1px solid rgba(255,107,107,0.25)", borderRadius: 16, fontSize: 12, color: "#ff9b9b", lineHeight: 1.5 }}>
              {String(playerError)}
            </div>
          )}
          {!playerReady ? (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>Connecting this device's speakers…</div>
          ) : !nowPlaying && (queue.length > 0 || fallback?.status === "ready") ? (
            <button onClick={handleStartMusic} style={{ padding: "12px 28px", borderRadius: 100, border: "none", background: "#1DB954", color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              ▶ Start music
            </button>
          ) : !nowPlaying ? (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>Speakers ready. Add a venue playlist below, or music starts when someone requests a song.</div>
          ) : (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>Playing through this device's speakers</div>
          )}
          <FallbackPlaylistPanel />
        </div>
      ) : (
        <div style={{ margin: "16px auto 0", maxWidth: 520, padding: "12px 24px", background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🎧</span>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>Audio plays through venue speakers. Vote on songs to control the queue.</span>
        </div>
      )}

      {upNext && (
        <div style={{ margin: "14px auto 0", maxWidth: 520, padding: "0 40px", fontSize: 12, color: "rgba(255,255,255,0.4)", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          Up next: <span style={{ color: "rgba(255,255,255,0.7)" }}>{upNext.title}</span> · {upNext.artist}
        </div>
      )}

      <div style={{ padding: "20px 40px 0", display: "flex", gap: 10, maxWidth: 600, margin: "0 auto", width: "100%" }}>
        <button className={`np-action-btn ${userVotedSkip ? "voted" : ""}`} onClick={handleSkipVote}>
          <span style={{ fontSize: 18 }}>⏭</span>
          <span>Vote Skip ({skipVotes}/{SKIP_VOTES_NEEDED})</span>
          <div className="skip-bar"><div className="skip-fill" style={{ width: `${Math.min(100, (skipVotes / SKIP_VOTES_NEEDED) * 100)}%` }} /></div>
        </button>
        <button className="np-action-btn" onClick={() => setShowPayModal(true)}>
          <span style={{ fontSize: 18 }}>💸</span>
          <span>Pay to Skip</span>
        </button>
      </div>

      <div style={{ padding: "16px 40px 0", display: "flex", flexDirection: "column", gap: 10, maxWidth: 600, margin: "0 auto", width: "100%" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.3)", textAlign: "center" }}>Rate the Vibe</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="vibe-btn" onClick={() => handleVibe("good")} style={{ border: `1px solid ${userVibeVote === "good" ? "rgba(29,185,84,0.3)" : "rgba(255,255,255,0.08)"}`, background: userVibeVote === "good" ? "rgba(29,185,84,0.15)" : "rgba(255,255,255,0.04)", color: userVibeVote === "good" ? "#1DB954" : "rgba(255,255,255,0.7)" }}>
            <span style={{ fontSize: 22 }}>🔥</span>
            <span>Good Vibe</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: userVibeVote === "good" ? "#1DB954" : "rgba(255,255,255,0.5)" }}>{vibeScore.good}</span>
          </button>
          <button className="vibe-btn" onClick={() => handleVibe("bad")} style={{ border: `1px solid ${userVibeVote === "bad" ? "rgba(220,38,38,0.3)" : "rgba(255,255,255,0.08)"}`, background: userVibeVote === "bad" ? "rgba(220,38,38,0.15)" : "rgba(255,255,255,0.04)", color: userVibeVote === "bad" ? "#fca5a5" : "rgba(255,255,255,0.7)" }}>
            <span style={{ fontSize: 22 }}>💀</span>
            <span>Killing Vibe</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: userVibeVote === "bad" ? "#fca5a5" : "rgba(255,255,255,0.5)" }}>{vibeScore.bad}</span>
          </button>
        </div>
        <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "10px 16px", display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>Total votes</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.6)" }}>{vibeScore.good + vibeScore.bad}</span>
        </div>
      </div>

      {showPayModal && (
        <div className="modal-overlay" onClick={() => setShowPayModal(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div style={{ width: 36, height: 4, background: "rgba(255,255,255,0.15)", borderRadius: 2, margin: "0 auto 20px" }} />
            <div style={{ fontSize: 18, fontWeight: 700, color: "#ffffff", marginBottom: 4, letterSpacing: -0.5 }}>Skip This Song</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 16, lineHeight: 1.5 }}>Pay to skip immediately</div>
            <div onClick={() => { onNotif("Song skipped ✓"); setShowPayModal(false); if (isVenueMode) handleSkip(); }}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.04)", cursor: "pointer", marginBottom: 8, transition: "all 0.2s" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#ffffff" }}>Skip Now</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>Move to next song</div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1DB954" }}>$1.99</div>
            </div>
            <button onClick={() => setShowPayModal(false)} style={{ width: "100%", padding: 14, borderRadius: 100, background: "rgba(255,255,255,0.06)", border: "none", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", color: "rgba(255,255,255,0.5)", transition: "all 0.2s" }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
